import { render } from 'engine/index';
import type { JackpotState } from 'shared/modelTypes/jackpot';
import { rpc } from 'src/backend';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { Button } from './Button';
import { CheckboxField, ToggleField } from './FormComponents';

export function PlayJackpot({ licenseCode }: { licenseCode: string }) {
	const view: HTMLElement = <div>Will play game {licenseCode}</div>;

	const sessionId = licenseCode;

	rpc.onAdminUpdates.subscribe(
		{ sessionId },
		{
			onData(data) {
				//
				if (data.kind === 'update-state') {
					const state = data.state as JackpotState;
					renderStage(state);
				}
			},
		},
	);

	const renderStage = (state: JackpotState) => {
		console.log('got state', state);
		const s = state.stage;
		switch (s) {
			case 'starting':
				render(view, <div class="animate-pulse text-xl text-gray-700">Starting play session</div>);
				break;
			case 'register-players':
				render(
					view,
					<PlayerRegisterView
						onContinue={() => {
							console.log('start the game');
							rpc.sendJackpotMessage.query({ sessionId, update: { kind: 'start-game' } });
						}}
						state={state}
					/>,
				);
				break;
			case 'round-intro':
				render(
					view,
					<div>
						<h3>Intro for round {(state.roundIndex ?? 0) + 1}</h3>
						<Button
							on:click={() => {
								rpc.sendJackpotMessage.query({ sessionId, update: { kind: 'start-round' } });
							}}
						>
							Begin Ronde
						</Button>
					</div>,
				);
				break;
			case 'round-outro':
				render(
					view,
					<div>
						<h3>Outro for round {(state.roundIndex ?? 0) + 1}</h3>
						<Button
							on:click={() => {
								rpc.sendJackpotMessage.query({ sessionId, update: { kind: 'end-round' } });
							}}
						>
							Ronde beëindigen
						</Button>
					</div>,
				);
				break;
			case 'play-round-turns':
				render(
					view,
					<div>
						<h3>
							Playing round {(state.roundIndex ?? 0) + 1}, turn {state.turnIndex + 1}
						</h3>{' '}
						<p>let players make their moves.</p>
						<div class="size-8"></div>
						<ToggleField
							compact
							name="lastRound"
							label="Laatste ronde"
							value={state.lastTurnInRound}
							onChange={(v) => {
								const lastRound = v || false;
								rpc.sendJackpotMessage.query({ sessionId, update: { kind: 'set-is-last-round', lastRound } });
							}}
						/>
						<hr class="dark:border-gray-900" />
						<div class="mt-2 w-fit rounded-md border border-theme-300 bg-theme-100 p-3 text-theme-950 dark:border-theme-800 dark:bg-theme-900 dark:text-theme-200">
							{(() => {
								switch (state.currentGameColor) {
									case undefined:
										return (
											<span>
												<strong>{state.players[state.currentPlayerIndex].name}</strong> is picking landing spot
											</span>
										);
									case 'yellow':
										return <span>playing positive card game (yellow)</span>;
									case 'red':
										return <span>playing negative card game (red)</span>;
									case 'green':
										return <span>playing desire card game (green)</span>;
									case 'blue':
										return <span>playing random event (blue)</span>;
									case 'purple':
										return <span>loterry guess (purple)</span>;
									case 'orange':
										return <span>playing sabotage game (start square)</span>;
								}
							})()}
						</div>
						<PlayerTable state={state} sessionId={sessionId} />
						<div class="flex h-80 gap-10 overflow-auto">
							<div class="flex flex-col gap-1">
								<strong>Good cards</strong>
								{state.goodCardPile.map((c) => (
									<span>{c.name}</span>
								))}
							</div>
							<div class="flex flex-col gap-1">
								<strong>Bad cards</strong>
								{state.badCardPile.map((c) => (
									<span>{c.name}</span>
								))}
							</div>
							<div class="flex flex-col gap-1">
								<strong>Desire cards</strong>
								{state.desireCardPile.map((c) => (
									<span>{c.name}</span>
								))}
							</div>
						</div>
						<div class="size-8"></div>
						<Button
							on:click={() => {
								rpc.sendJackpotMessage.query({ sessionId, update: { kind: 'force-restart-round' } });
							}}
						>
							Forceer nieuwe ronde start
						</Button>
					</div>,
				);
				break;
			case 'ending-report':
				render(
					view,
					<div>
						showing end report.
						<PlayerTable state={state} sessionId={sessionId} />
					</div>,
				);
				break;
		}
	};

	// render initial state
	(async () => {
		const state = await rpc.getGameState.query({ sessionId });
		renderStage(state);
	})();

	return view;
}

function PlayerRegisterView({ state, onContinue }: { state: JackpotState; onContinue: () => void }) {
	console.log('render players', state.players);

	const playersWithName = state.players.filter((p) => p.name);

	return (
		<div>
			<h3 class="text-lg font-bold text-gray-800 dark:text-gray-200">Spelers registreren</h3>
			<p class="mb-5 text-gray-700">
				Laat spelers de QR code op het bord scannen en vervolgens hun gegevens invullen.
				<br />
				Druk op <strong>doorgaan</strong> als alle spelers gereed zijn.
			</p>
			{playersWithName.length > 0 && (
				<Table>
					<thead>
						<TrHead>
							<Th>Name</Th>
							<Th>Color</Th>
							<Th>Id</Th>
						</TrHead>
					</thead>
					<tbody>
						{playersWithName.map((player) => {
							return (
								<TrBody>
									<Td>{player.name}</Td>
									<Td>{player.color}</Td>
									<Td>{player.id}</Td>
								</TrBody>
							);
						})}
					</tbody>
				</Table>
			)}
			{!playersWithName.length && <small class="text-gray-700">Er zijn nog geen spelers.</small>}
			<div class="mt-5"></div>
			<Button on:click={onContinue}>Doorgaan</Button>
		</div>
	);
}

function Card({ name }: { name: string }) {
	return <div class={'mb-1 rounded-md border bg-white p-1 text-sm text-gray-800 shadow-sm'}>{name}</div>;
}

function PlayerTable({ sessionId, state }: { sessionId: string; state: JackpotState }) {
	return (
		<>
			<div class="flex flex-col">
				<span>Sabotaged jackpot indices:</span>

				{state.roundIndex === 0 ? (
					<div class="flex flex-wrap items-center gap-2">
						{state.sabotagedJackpotSpots.map((l) => (
							<JackpotFruit key={l} name={l} />
						))}
					</div>
				) : (
					<span>{state.sabotagedJackpotSpots.join(', ')}</span>
				)}
			</div>

			<h3 class="mb-2 mt-5 text-lg font-bold text-gray-900 dark:text-gray-200">Players:</h3>
			<Table>
				<thead>
					<TrHead>
						<Th title="When disabled the players turn will always be skipped">Active</Th>
						<Th>Name</Th>
						<Th>Color</Th>
						<Th>Score</Th>
						<Th>Tickets</Th>
						<Th>Good Cards</Th>
						<Th>Bad Cards</Th>
						<Th>Desire Cards</Th>
						<Th>Id</Th>
					</TrHead>
				</thead>
				<tbody>
					{state.players.map((player) => {
						console.log(player);
						const roundData = player.roundData[state.roundIndex];
						return (
							<TrBody>
								<Td>
									<CheckboxField
										name={player.id + '-active'}
										label={''}
										value={player.active}
										onChange={(v) => {
											rpc.sendJackpotMessage.query({
												sessionId,
												update: { kind: 'set-player-active', playerId: player.id, active: v ?? true },
											});
										}}
									/>
								</Td>
								<Td>{player.name}</Td>
								<Td>{player.color}</Td>
								<Td>{player.score}</Td>
								<Td>
									{state.roundIndex === 0 ? (
										<div class="flex flex-wrap items-center gap-2">
											{roundData.lotteryTickets.map((l) => (
												<JackpotFruit name={l.name} />
											))}
										</div>
									) : roundData ? (
										roundData.lotteryTickets.map((l) => l.name).join(', ')
									) : (
										''
									)}
								</Td>
								<Td>{roundData ? roundData.goodCards.map((c) => <Card {...c} />) : ''}</Td>
								<Td>{roundData ? roundData.badCards.map((c) => <Card {...c} />) : ''}</Td>
								<Td>{roundData ? roundData.desireCards.map((c) => <Card {...c} />) : ''}</Td>
								<Td>{player.id}</Td>
							</TrBody>
						);
					})}

					<TrBody>
						<Td></Td>
						<Td>
							<strong>Totaal:</strong>
						</Td>
						<Td>{state.players.reduce((acc, cur) => cur.score + acc, 0)}</Td>

						<Td></Td>
						<Td></Td>
						<Td></Td>
					</TrBody>
				</tbody>
			</Table>
		</>
	);
}

export function JackpotFruit({ name }: { name: string }) {
	return (
		<div class="overflow-hidden rounded-md border border-gray-300 text-theme-50 shadow-md">
			<div
				class="size-16 bg-[url(/images/slotreel.webp)] bg-cover"
				style={`background-position: 0px ${(100 / 8) * parseInt(name.replace('fruit-', ''))}%`}
			></div>
		</div>
	);
}
